import { useState, useRef, useEffect, useCallback } from 'react';
import {
  Send, Loader2, FileText, ClipboardCheck, CheckCircle2,
  Plus, Pencil, Trash2, MessageSquare, Network, CloudUpload,
} from 'lucide-react';
import yaml from 'js-yaml';
import Editor from 'react-simple-code-editor';
import { highlight, languages } from 'prismjs';
import 'prismjs/components/prism-yaml';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Card, CardContent, CardHeader } from '../ui/Card';
import { MarkdownPreview } from '../ui/MarkdownPreview';
import { cn } from '../../lib/utils';
import { apiClient } from '../../lib/api-client';
import { SynergyPlanDialog } from './SynergyPlanDialog';
import { ReconciliationReportDialog } from './ReconciliationReportDialog';
import { DraftHierarchyDialog } from './DraftHierarchyDialog';
import { ApplyStreamCompareDialog } from './ApplyStreamCompareDialog';

interface AiStreamSummary {
  streamId: string;
  displayName: string;
  enabled: boolean;
}

interface OrgDraftChatProps {
  existingStreams: AiStreamSummary[];
  onApplied?: () => void;
  /** After Apply Stream succeeds: refresh data and switch parent to Hierarchy tab. */
  onStreamApplyNavigate?: () => void;
  className?: string;
}

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

interface SessionSummary {
  id: string;
  title: string;
  userId: string;
  status: string;
  streamCount: number;
  messageCount: number;
  createdAt: string;
}

type SessionStatus = 'interviewing' | 'yaml_generated' | 'plan_generated' | 'accepted';

type CursorCatalogLevel = { id: string; label: string };
type CursorCatalogModel = { id: string; label?: string; levels?: CursorCatalogLevel[] };

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function OrgDraftChat({ existingStreams, onApplied, onStreamApplyNavigate, className }: OrgDraftChatProps) {
  // Session list state
  const [sessionList, setSessionList] = useState<SessionSummary[]>([]);
  const [loadingSessions, setLoadingSessions] = useState(true);

  // Active session state
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [draftYamls, setDraftYamls] = useState<Record<string, string>>({});
  const [seedYamls, setSeedYamls] = useState<Record<string, string>>({});
  const [status, setStatus] = useState<SessionStatus>('interviewing');
  const [synergyPlanMd, setSynergyPlanMd] = useState<string | null>(null);

  // UI state
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [starting, setStarting] = useState(false);
  const [generatingPlan, setGeneratingPlan] = useState(false);
  const [activeYamlTab, setActiveYamlTab] = useState<string>('');
  const [selectedModelId, setSelectedModelId] = useState('');
  const [selectedModelLevel, setSelectedModelLevel] = useState('');
  const [cursorModels, setCursorModels] = useState<CursorCatalogModel[]>([]);
  const [cursorModelsLoading, setCursorModelsLoading] = useState(false);
  const [cursorModelsError, setCursorModelsError] = useState<string | null>(null);
  const [streamSteps, setStreamSteps] = useState<string[]>([]);
  const [selectedSeedStreams, setSelectedSeedStreams] = useState<Set<string>>(new Set());
  const [showSynergyPlan, setShowSynergyPlan] = useState(false);
  const [showReconciliation, setShowReconciliation] = useState(false);
  const [showHierarchy, setShowHierarchy] = useState(false);
  const [reconciliationData, setReconciliationData] = useState<any>(null);
  const [loadingReconciliation, setLoadingReconciliation] = useState(false);
  const [applying, setApplying] = useState(false);
  const [showNewSession, setShowNewSession] = useState(false);
  const [showApplyCompare, setShowApplyCompare] = useState(false);
  const [applyStreamBusy, setApplyStreamBusy] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  /** While POST /draft/message is in flight, poll Redis so YAML updates from tool checkpoints appear before the HTTP response returns. */
  const draftSendPollRef = useRef<{
    lenBefore: number;
    sessionId: string;
    yamlsBeforeJson: string;
  } | null>(null);
  const draftHandledByPollRef = useRef(false);
  const draftPollAbortRef = useRef<AbortController | null>(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => { scrollToBottom(); }, [messages, scrollToBottom]);

  const streamIds = Object.keys(draftYamls);

  useEffect(() => {
    if (streamIds.length > 0 && !streamIds.includes(activeYamlTab)) {
      setActiveYamlTab(streamIds[0]);
    }
  }, [streamIds, activeYamlTab]);

  // ── Load session list on mount ────────────────────────────────

  const loadSessionList = useCallback(async () => {
    try {
      const res = await apiClient.listDraftSessions();
      setSessionList(res.sessions || []);
    } catch {
      // Silently ignore list failures
    } finally {
      setLoadingSessions(false);
    }
  }, []);

  useEffect(() => { void loadSessionList(); }, [loadSessionList]);

  useEffect(() => {
    if (!sending || !sessionId) return;
    const meta = draftSendPollRef.current;
    if (!meta || meta.sessionId !== sessionId) return;

    const tick = async () => {
      const m = draftSendPollRef.current;
      if (!m || m.sessionId !== sessionId) return;
      try {
        const s = await apiClient.getDraftSession(sessionId);
        setDraftYamls((prev) => {
          if (JSON.stringify(prev) === JSON.stringify(s.draftYamls)) return prev;
          return s.draftYamls;
        });
        setStatus(s.status as SessionStatus);
        if (s.synergyPlanMd) setSynergyPlanMd(s.synergyPlanMd);

        const assistantSaved =
          s.messages.length >= m.lenBefore + 2 && s.agentRunState !== 'running';

        if (assistantSaved) {
          draftHandledByPollRef.current = true;
          setMessages(s.messages);
          draftPollAbortRef.current?.abort();
        }
      } catch {
        /* ignore transient poll errors */
      }
    };

    void tick();
    const id = window.setInterval(tick, 3500);
    return () => window.clearInterval(id);
  }, [sending, sessionId]);

  useEffect(() => {
    let cancelled = false;
    async function loadModels() {
      setCursorModelsLoading(true);
      setCursorModelsError(null);
      try {
        const response = await apiClient.getDraftModels();
        if (cancelled) return;
        const models = Array.isArray(response?.cursor?.models) ? response.cursor.models : [];
        setCursorModels(models);
        const nextModel = models.some((model) => model.id === selectedModelId)
          ? selectedModelId
          : (models[0]?.id || '');
        setSelectedModelId(nextModel);
        const levels = models.find((model) => model.id === nextModel)?.levels || [];
        setSelectedModelLevel(levels.some((level) => level.id === selectedModelLevel) ? selectedModelLevel : (levels[0]?.id || ''));
      } catch (error: any) {
        if (cancelled) return;
        setCursorModels([]);
        setCursorModelsError(error.message || 'Failed to load Cursor models');
      } finally {
        if (!cancelled) setCursorModelsLoading(false);
      }
    }
    void loadModels();
    return () => { cancelled = true; };
  }, []);

  const selectedModelDef = cursorModels.find((model) => model.id === selectedModelId);
  const selectedLevelOptions = selectedModelDef?.levels || [];

  useEffect(() => {
    if (selectedLevelOptions.length === 0) {
      if (selectedModelLevel) setSelectedModelLevel('');
      return;
    }
    if (!selectedLevelOptions.some((level) => level.id === selectedModelLevel)) {
      setSelectedModelLevel(selectedLevelOptions[0].id);
    }
  }, [selectedLevelOptions, selectedModelLevel]);

  // ── Session lifecycle ─────────────────────────────────────────

  async function resumeSession(id: string) {
    try {
      const res = await apiClient.getDraftSession(id);
      setSessionId(res.id);
      setMessages(res.messages);
      setDraftYamls(res.draftYamls);
      setSeedYamls(res.seedStreamIds.length > 0 ? { ...res.draftYamls } : {});
      setStatus(res.status as SessionStatus);
      setSynergyPlanMd(res.synergyPlanMd);
      setShowNewSession(false);
    } catch (err: any) {
      console.error('Failed to resume session:', err);
    }
  }

  async function startSession(seedStreamIds?: string[]) {
    setStarting(true);
    try {
      const res = await apiClient.startDraftSession(seedStreamIds);
      setSessionId(res.sessionId);
      setDraftYamls(res.draftYamls);
      setSeedYamls({ ...res.draftYamls });
      setStatus(res.status as SessionStatus);
      setMessages([{ role: 'assistant', content: res.greeting }]);
      setShowNewSession(false);
      void loadSessionList();
    } catch (err: any) {
      console.error('Failed to start draft session:', err);
    } finally {
      setStarting(false);
    }
  }

  async function deleteSession(id: string) {
    try {
      await apiClient.deleteDraftSession(id);
      if (sessionId === id) {
        setSessionId(null);
        setMessages([]);
        setDraftYamls({});
        setSeedYamls({});
        setStatus('interviewing');
        setSynergyPlanMd(null);
      }
      void loadSessionList();
    } catch (err: any) {
      console.error('Failed to delete session:', err);
    }
  }

  async function persistSynergyMarkdown(next: string) {
    setSynergyPlanMd(next);
    if (!sessionId) return;
    try {
      await apiClient.saveDraftSynergyPlan(sessionId, next);
    } catch (err) {
      console.error('Failed to save synergy plan:', err);
    }
  }

  async function sendMessage() {
    if (!sessionId || !input.trim() || sending) return;
    const userMsg = input.trim();
    const lenBefore = messages.length;
    const yamlsBeforeJson = JSON.stringify(draftYamls);
    setInput('');
    setMessages((prev) => [...prev, { role: 'user', content: userMsg }, { role: 'assistant', content: '' }]);
    draftHandledByPollRef.current = false;
    draftPollAbortRef.current = new AbortController();
    draftSendPollRef.current = { lenBefore, sessionId, yamlsBeforeJson };
    setSending(true);
    setStreamSteps([]);
    try {
      const appendStep = (label: string) => {
        setStreamSteps((prev) => (prev[prev.length - 1] === label ? prev : [...prev, label].slice(-8)));
      };
      await apiClient.streamDraftMessage(
        sessionId,
        userMsg,
        { model: selectedModelId || undefined, cursorEffort: selectedModelLevel || undefined },
        (event) => {
          if (event?.type === 'status') {
            appendStep(String(event.message || event.status || 'Working'));
            return;
          }
          if (event?.type === 'tool_start') {
            appendStep(`Tool: ${event.name || 'running'}`);
            return;
          }
          if (event?.type === 'assistant_delta' && event.text) {
            setMessages((prev) => {
              const next = [...prev];
              const last = next[next.length - 1];
              if (last?.role === 'assistant') {
                next[next.length - 1] = { ...last, content: `${last.content || ''}${event.text}` };
              }
              return next;
            });
            return;
          }
          if (event?.type === 'session') {
            if (event.draftYamls) setDraftYamls(event.draftYamls);
            if (event.status) setStatus(event.status as SessionStatus);
            if (event.synergyPlanMd) setSynergyPlanMd(event.synergyPlanMd);
            return;
          }
          if (event?.type === 'done' && event.output) {
            setMessages((prev) => {
              const next = [...prev];
              const last = next[next.length - 1];
              if (last?.role === 'assistant') {
                next[next.length - 1] = { ...last, content: event.output };
              }
              return next;
            });
          }
          if (event?.type === 'error') {
            throw new Error(event.error || 'Draft chat failed');
          }
        },
        draftPollAbortRef.current.signal,
      );
      setMessages((prev) => {
        const last = prev[prev.length - 1];
        if (last?.role === 'assistant' && !String(last.content || '').trim()) {
          return [
            ...prev.slice(0, -1),
            {
              role: 'assistant',
              content: '_The model updated YAML via tools but returned no text. Check the stream tabs on the right._',
            },
          ];
        }
        return prev;
      });
    } catch (err: any) {
      if (draftHandledByPollRef.current) {
        return;
      }
      let recovered = false;
      try {
        const s = await apiClient.getDraftSession(sessionId);
        if (s.messages.length >= lenBefore + 2) {
          setMessages(s.messages);
          setDraftYamls(s.draftYamls);
          setStatus(s.status as SessionStatus);
          if (s.synergyPlanMd) setSynergyPlanMd(s.synergyPlanMd);
          recovered = true;
        } else if (JSON.stringify(s.draftYamls) !== yamlsBeforeJson) {
          setDraftYamls(s.draftYamls);
          setStatus(s.status as SessionStatus);
          if (s.synergyPlanMd) setSynergyPlanMd(s.synergyPlanMd);
          setMessages((prev) => [
            ...prev,
            {
              role: 'assistant',
              content:
                '_Recovered updated YAML from the server (the browser did not receive the full response). Chat history may be incomplete; refresh the session from the sidebar if needed._',
            },
          ]);
          recovered = true;
        }
      } catch {
        /* ignore */
      }
      if (!recovered) {
        const msg = err?.message || 'Failed to get response';
        const friendly =
          msg === 'REQUEST_TIMEOUT'
            ? 'The request timed out. If YAML or a reply appeared above after a refresh, the run may have finished on the server—try resuming the session from the sidebar.'
            : msg;
        setMessages((prev) => [...prev, { role: 'assistant', content: `Error: ${friendly}` }]);
      }
    } finally {
      draftSendPollRef.current = null;
      draftPollAbortRef.current = null;
      setSending(false);
      inputRef.current?.focus();
    }
  }

  async function handleGeneratePlan() {
    if (!sessionId) return;
    setGeneratingPlan(true);
    try {
      const res = await apiClient.generateSynergyPlan(sessionId);
      setSynergyPlanMd(res.markdown);
      setStatus('plan_generated');
      setShowSynergyPlan(true);
    } catch (err: any) {
      console.error('Failed to generate synergy plan:', err);
    } finally {
      setGeneratingPlan(false);
    }
  }

  async function handleReconciliation() {
    if (!sessionId) return;
    setLoadingReconciliation(true);
    try {
      const res = await apiClient.getDraftReconciliationPreview(sessionId);
      setReconciliationData(res);
      setShowReconciliation(true);
    } catch (err: any) {
      console.error('Failed to get reconciliation preview:', err);
    } finally {
      setLoadingReconciliation(false);
    }
  }

  async function handleApplyStreamClick() {
    const sid = activeYamlTab;
    if (!sid || !draftYamls[sid]) return;
    const y = draftYamls[sid];
    let parsed: any;
    try {
      parsed = yaml.load(y);
    } catch (e: any) {
      window.alert(`Invalid YAML: ${e.message}`);
      return;
    }
    const root = parsed?.stream || parsed || {};
    const yamlId = String(root.id || root.streamId || '').trim();
    if (yamlId && yamlId !== sid) {
      window.alert(`YAML stream id "${yamlId}" must match the selected stream tab "${sid}".`);
      return;
    }
    const existsOnPortal = existingStreams.some((s) => s.streamId === sid);
    if (existsOnPortal) {
      setShowApplyCompare(true);
      return;
    }
    setApplyStreamBusy(true);
    try {
      await apiClient.createAiConfigStream(sid, y);
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: `Stream **${sid}** was created in the repository. Open the Hierarchy tab to see the org chart.`,
        },
      ]);
      onApplied?.();
      onStreamApplyNavigate?.();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to create stream';
      window.alert(msg);
    } finally {
      setApplyStreamBusy(false);
    }
  }

  function handleCompareApplied(finalYaml: string) {
    setDraftYamls((prev) => ({ ...prev, [activeYamlTab]: finalYaml }));
    setMessages((prev) => [
      ...prev,
      {
        role: 'assistant',
        content: `Stream **${activeYamlTab}** was updated in the repository. Open the Hierarchy tab to see the org chart.`,
      },
    ]);
    onApplied?.();
    onStreamApplyNavigate?.();
  }

  async function handleApply(selectedStepIds: string[]) {
    if (!sessionId) return;
    setApplying(true);
    try {
      await apiClient.applyDraftSession(sessionId, selectedStepIds);
      setStatus('accepted');
      setShowReconciliation(false);
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: 'Draft configurations have been applied successfully. You can view the updated streams in the Hierarchy or Grid tabs.' },
      ]);
      onApplied?.();
    } catch (err: any) {
      console.error('Failed to apply draft:', err);
    } finally {
      setApplying(false);
    }
  }

  function toggleSeedStream(id: string) {
    setSelectedSeedStreams((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function isYamlModified(streamId: string): boolean {
    return seedYamls[streamId] !== undefined && seedYamls[streamId] !== draftYamls[streamId];
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  }

  function formatTime(iso: string): string {
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffH = diffMs / 3_600_000;
    if (diffH < 1) return `${Math.round(diffMs / 60_000)}m ago`;
    if (diffH < 24) return `${Math.round(diffH)}h ago`;
    return d.toLocaleDateString();
  }

  // ── Render ────────────────────────────────────────────────────

  return (
    <>
      <div
        className={cn(
          'flex min-h-0 min-w-0 w-full max-w-full flex-1 items-stretch gap-4 overflow-hidden',
          className,
        )}
      >
        {/* Session sidebar */}
        <div className="flex min-h-0 w-56 shrink-0 flex-col self-stretch overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900">
          <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
            <Button
              variant="outline"
              size="sm"
              className="w-full"
              onClick={() => { setShowNewSession(true); setSessionId(null); }}
            >
              <Plus className="w-3.5 h-3.5 mr-1.5" />
              New Session
            </Button>
          </div>
          <div className="flex-1 overflow-y-auto">
            {loadingSessions ? (
              <div className="p-4 text-center text-xs text-gray-400">Loading...</div>
            ) : sessionList.length === 0 ? (
              <div className="p-4 text-center text-xs text-gray-400">No sessions yet</div>
            ) : (
              sessionList.map((s) => (
                <div
                  key={s.id}
                  className={cn(
                    'group flex items-start gap-2 px-3 py-2.5 cursor-pointer border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors',
                    sessionId === s.id && 'bg-violet-50 dark:bg-violet-900/20',
                  )}
                  onClick={() => resumeSession(s.id)}
                >
                  <MessageSquare className="w-3.5 h-3.5 mt-0.5 text-gray-400 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-gray-900 dark:text-gray-100 truncate">
                      {s.title}
                    </p>
                    <p className="text-[10px] text-gray-400 dark:text-gray-500 truncate">
                      {s.userId || 'unknown'}
                    </p>
                    <p className="text-[10px] text-gray-400 dark:text-gray-500">
                      {s.messageCount} msgs &middot; {s.streamCount} streams &middot; {formatTime(s.createdAt)}
                    </p>
                  </div>
                  <button
                    className="opacity-0 group-hover:opacity-100 p-0.5 text-gray-400 hover:text-red-500 transition-all"
                    onClick={(e) => { e.stopPropagation(); deleteSession(s.id); }}
                    title="Delete session"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Main area */}
        {!sessionId && !showNewSession ? (
          /* Prompt to select or create */
          <div className="flex min-h-0 min-w-0 flex-1 items-center justify-center overflow-y-auto">
            <Card className="max-w-md w-full">
              <CardContent className="p-8 text-center space-y-4">
                <MessageSquare className="w-10 h-10 mx-auto text-gray-300 dark:text-gray-600" />
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Select an existing session from the sidebar or create a new one.
                </p>
                <Button variant="outline" onClick={() => setShowNewSession(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  New Session
                </Button>
              </CardContent>
            </Card>
          </div>
        ) : !sessionId && showNewSession ? (
          /* New session launcher */
          <div className="flex min-h-0 min-w-0 flex-1 items-center justify-center overflow-y-auto">
            <Card className="max-w-lg w-full">
              <CardHeader className="pb-2">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">New Session</h2>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button
                  className="w-full justify-start gap-3 h-auto py-3"
                  variant="outline"
                  onClick={() => startSession()}
                  disabled={starting}
                >
                  <Plus className="w-5 h-5" />
                  <div className="text-left">
                    <div className="font-medium">Start from scratch</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      Build a new organization setup from the ground up
                    </div>
                  </div>
                </Button>

                {existingStreams.length > 0 && (
                  <div className="space-y-2">
                    <Button
                      className="w-full justify-start gap-3 h-auto py-3"
                      variant="outline"
                      onClick={() => startSession(Array.from(selectedSeedStreams))}
                      disabled={starting || selectedSeedStreams.size === 0}
                    >
                      <Pencil className="w-5 h-5" />
                      <div className="text-left">
                        <div className="font-medium">Edit existing org</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          Load and modify selected streams ({selectedSeedStreams.size} selected)
                        </div>
                      </div>
                    </Button>
                    <div className="pl-4 space-y-1.5 max-h-48 overflow-y-auto">
                      {existingStreams.map((stream) => (
                        <label key={stream.streamId} className="flex items-center gap-2 text-sm cursor-pointer py-1">
                          <input
                            type="checkbox"
                            checked={selectedSeedStreams.has(stream.streamId)}
                            onChange={() => toggleSeedStream(stream.streamId)}
                            className="rounded border-gray-300 dark:border-gray-600"
                          />
                          <span className="text-gray-700 dark:text-gray-300">{stream.displayName}</span>
                          {!stream.enabled && <Badge variant="outline" className="text-[10px]">Disabled</Badge>}
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {starting && (
                  <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Starting session...
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        ) : (
          /* Active session: wrapper must be one flex child so row height is bounded (Fragment breaks min-h-0). */
          <div className="flex min-h-0 min-w-0 flex-1 gap-4 overflow-hidden">
            {/* Chat panel */}
            <div className="flex min-h-0 min-w-0 flex-[3] flex-col overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900">
              <div className="min-h-0 flex-1 basis-0 overflow-y-auto overscroll-contain p-4 space-y-4">
                {messages.map((msg, i) => (
                  <div
                    key={i}
                    className={cn('flex', msg.role === 'user' ? 'justify-end' : 'justify-start')}
                  >
                    <div
                      className={cn(
                        'max-w-[85%] rounded-lg px-4 py-2.5 text-sm',
                        msg.role === 'user'
                          ? 'bg-violet-600 text-white'
                          : 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100',
                      )}
                    >
                      {msg.role === 'assistant' ? (
                        <MarkdownPreview
                          content={msg.content}
                          variant="chat"
                          className="text-sm [&_.prose]:text-sm"
                        />
                      ) : (
                        <p className="whitespace-pre-wrap">{msg.content}</p>
                      )}
                    </div>
                  </div>
                ))}
                {sending && (
                  <div className="flex justify-start">
                    <div className="bg-gray-100 dark:bg-gray-800 rounded-lg px-4 py-2.5 max-w-[90%] space-y-1">
                      <div className="flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin text-gray-400 shrink-0" />
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          Cursor agent run in progress…
                        </span>
                      </div>
                      {streamSteps.length > 0 ? (
                        <p className="text-[11px] text-gray-400 dark:text-gray-500 leading-snug">
                          {streamSteps[streamSteps.length - 1]}
                        </p>
                      ) : (
                        <p className="text-[11px] text-gray-400 dark:text-gray-500 leading-snug">
                          Stream YAML syncs from the server as tools run; check the tabs on the right before the reply finishes.
                        </p>
                      )}
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              <div className="shrink-0 border-t border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900 p-3 space-y-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <select
                    value={selectedModelId}
                    onChange={(e) => setSelectedModelId(e.target.value)}
                    disabled={sending || cursorModelsLoading}
                    className="text-xs rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 px-2 py-1 max-w-[220px] focus:outline-none focus:ring-1 focus:ring-violet-500"
                  >
                    {cursorModelsLoading ? <option value="">Loading models...</option> : null}
                    {!cursorModelsLoading && cursorModels.length === 0 ? <option value="">No models</option> : null}
                    {cursorModels.map((model) => (
                      <option key={model.id} value={model.id}>{model.label || model.id}</option>
                    ))}
                  </select>
                  <select
                    value={selectedModelLevel}
                    onChange={(e) => setSelectedModelLevel(e.target.value)}
                    disabled={sending || cursorModelsLoading || selectedLevelOptions.length === 0}
                    className="text-xs rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 px-2 py-1 max-w-[180px] focus:outline-none focus:ring-1 focus:ring-violet-500"
                  >
                    {selectedLevelOptions.length === 0 ? <option value="">No levels</option> : null}
                    {selectedLevelOptions.map((level) => (
                      <option key={level.id} value={level.id}>{level.label || level.id}</option>
                    ))}
                  </select>
                  <span className="text-[10px] text-gray-400 dark:text-gray-500">Cursor model</span>
                  {cursorModelsError ? (
                    <span className="text-[10px] text-red-600 dark:text-red-400">{cursorModelsError}</span>
                  ) : null}
                </div>
                <div className="flex gap-2">
                  <textarea
                    ref={inputRef}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Describe your organization..."
                    className="flex-1 resize-none rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-violet-500"
                    rows={2}
                    disabled={sending || status === 'accepted'}
                  />
                  <Button
                    onClick={sendMessage}
                    disabled={!input.trim() || sending || status === 'accepted'}
                    className="self-end"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>

            {/* YAML panel */}
            <div className="flex min-h-0 min-w-0 flex-[2] flex-col overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900">
              <div className="shrink-0 flex items-center gap-2 px-3 py-2 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 flex-wrap">
                <Button variant="outline" size="sm" onClick={handleGeneratePlan} disabled={streamIds.length === 0 || generatingPlan}>
                  {generatingPlan ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <FileText className="w-3.5 h-3.5 mr-1.5" />}
                  Synergy Plan
                </Button>
                <Button variant="outline" size="sm" onClick={() => setShowHierarchy(true)} disabled={streamIds.length === 0}>
                  <Network className="w-3.5 h-3.5 mr-1.5" />
                  Hierarchy
                </Button>
                <Button variant="outline" size="sm" onClick={handleReconciliation} disabled={streamIds.length === 0 || loadingReconciliation}>
                  {loadingReconciliation ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <ClipboardCheck className="w-3.5 h-3.5 mr-1.5" />}
                  Reconciliation
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => void handleApplyStreamClick()}
                  disabled={!activeYamlTab || !draftYamls[activeYamlTab] || applyStreamBusy}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  {applyStreamBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <CloudUpload className="w-3.5 h-3.5 mr-1.5" />}
                  Apply Stream
                </Button>
                {status === 'accepted' && (
                  <Badge variant="success" className="ml-auto">
                    <CheckCircle2 className="w-3 h-3 mr-1" />
                    Applied
                  </Badge>
                )}
              </div>

              {streamIds.length > 0 && (
                <div className="shrink-0 flex gap-1 px-3 py-1.5 border-b border-gray-200 dark:border-gray-700 overflow-x-auto">
                  {streamIds.map((id) => (
                    <button
                      key={id}
                      onClick={() => setActiveYamlTab(id)}
                      className={cn(
                        'px-2.5 py-1 rounded text-xs font-medium transition-colors whitespace-nowrap flex items-center gap-1.5',
                        activeYamlTab === id
                          ? 'bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300'
                          : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800',
                      )}
                    >
                      {id}
                      {isYamlModified(id) && <span className="w-1.5 h-1.5 rounded-full bg-yellow-500" />}
                    </button>
                  ))}
                </div>
              )}

              <div className="min-h-0 flex-1 basis-0 overflow-auto overscroll-contain">
                {streamIds.length === 0 ? (
                  <div className="flex h-full min-h-[8rem] items-center justify-center px-6 text-center text-sm text-gray-400 dark:text-gray-500">
                    YAML configurations will appear here as the agent generates them.
                  </div>
                ) : activeYamlTab && draftYamls[activeYamlTab] ? (
                  <Editor
                    value={draftYamls[activeYamlTab]}
                    onValueChange={() => {}}
                    highlight={(code) => highlight(code, languages.yaml, 'yaml')}
                    padding={16}
                    className="min-w-0 text-xs font-mono pointer-events-none"
                    style={{
                      fontFamily: 'ui-monospace, monospace',
                      fontSize: '12px',
                      minHeight: '100%',
                    }}
                  />
                ) : (
                  <div className="flex h-full items-center justify-center text-sm text-gray-400 dark:text-gray-500">
                    Select a stream tab to view its YAML.
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <DraftHierarchyDialog
        open={showHierarchy}
        onOpenChange={setShowHierarchy}
        draftYamls={draftYamls}
        initialStreamId={activeYamlTab || undefined}
        onDraftYamlChange={(streamId, yaml) => {
          setDraftYamls((prev) => ({ ...prev, [streamId]: yaml }));
        }}
        chat={{
          messages,
          input,
          sending,
          disabled: status === 'accepted',
          statusLine: streamSteps[streamSteps.length - 1],
          onInputChange: setInput,
          onSend: () => { void sendMessage(); },
        }}
      />

      {activeYamlTab && draftYamls[activeYamlTab] && (
        <ApplyStreamCompareDialog
          open={showApplyCompare}
          onOpenChange={setShowApplyCompare}
          streamId={activeYamlTab}
          draftYaml={draftYamls[activeYamlTab]}
          onApplied={handleCompareApplied}
        />
      )}

      {/* Synergy Plan Dialog */}
      {synergyPlanMd && (
        <SynergyPlanDialog
          open={showSynergyPlan}
          onOpenChange={setShowSynergyPlan}
          markdown={synergyPlanMd}
          onMarkdownChange={(next) => { void persistSynergyMarkdown(next); }}
          onContinueEditing={() => inputRef.current?.focus()}
          onProceedToReconciliation={handleReconciliation}
          chat={{
            messages,
            input,
            sending,
            disabled: status === 'accepted',
            statusLine: streamSteps[streamSteps.length - 1],
            onInputChange: setInput,
            onSend: () => { void sendMessage(); },
          }}
        />
      )}

      {/* Reconciliation Report Dialog */}
      {reconciliationData && (
        <ReconciliationReportDialog
          open={showReconciliation}
          onOpenChange={setShowReconciliation}
          streams={reconciliationData.streams || []}
          onApply={handleApply}
          applying={applying}
        />
      )}
    </>
  );
}
